import { type ChatMsg, CONFIG } from "@agency/shared";
import { net } from "../net/NetClient.js";
import { setGameKeyboardEnabled } from "../runtime.js";
import { store } from "../store/GameStore.js";
import { button, clear, el } from "./dom.js";

type ChatTab = "player" | "system";

interface TabView {
  button: HTMLButtonElement;
  badge: HTMLElement;
  unread: number;
}

export class ChatPanel {
  private readonly root: HTMLElement;
  private readonly log: HTMLElement;
  private readonly input: HTMLInputElement;
  private readonly collapseBtn: HTMLButtonElement;
  private readonly tabs: Record<ChatTab, TabView>;
  private active: ChatTab = "player";
  private collapsed = false;

  constructor(parent: HTMLElement) {
    this.root = el("div", "panel chat-panel hidden");

    const header = el("div", "chat-header");
    this.tabs = {
      player: this.makeTab("player", "Chat"),
      system: this.makeTab("system", "System"),
    };
    this.collapseBtn = button("▾", "chat-collapse");
    this.collapseBtn.title = "Collapse chat";
    this.collapseBtn.addEventListener("click", () => this.toggleCollapsed());
    header.append(this.tabs.player.button, this.tabs.system.button, this.collapseBtn);

    this.log = el("div", "chat-log");
    this.log.dataset.tab = this.active;

    const inputRow = el("div", "chat-input-row");
    this.input = el("input");
    this.input.type = "text";
    this.input.maxLength = CONFIG.CHAT_MAX_LEN;
    this.input.placeholder = "Say something…";
    const sendBtn = button("➤", "chat-send");
    sendBtn.title = "Send";
    sendBtn.setAttribute("aria-label", "Send");
    // Keep focus in the input so the game keyboard stays disabled while chatting.
    sendBtn.addEventListener("mousedown", (event) => event.preventDefault());
    sendBtn.addEventListener("click", () => this.send());
    inputRow.append(this.input, sendBtn);

    this.root.append(header, this.log, inputRow);
    parent.appendChild(this.root);

    this.input.addEventListener("focus", () => setGameKeyboardEnabled(false));
    this.input.addEventListener("blur", () => setGameKeyboardEnabled(true));
    this.input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        // Keep the window-level Enter handler from immediately re-focusing the input.
        event.stopPropagation();
        this.send();
        this.input.blur();
      } else if (event.key === "Escape") {
        event.preventDefault();
        this.input.blur();
      }
    });
  }

  mount(): void {
    store.on("chat", (msg) => this.add(msg, true));
    store.on("welcome", (welcome) => {
      clear(this.log);
      for (const msg of welcome.chatHistory) this.add(msg, false);
    });
    if (store.lastWelcome) {
      clear(this.log);
      for (const msg of store.lastWelcome.chatHistory) this.add(msg, false);
    }
  }

  show(): void {
    this.root.classList.remove("hidden");
  }

  hide(): void {
    this.input.blur();
    this.root.classList.add("hidden");
  }

  focusInput(): void {
    if (this.collapsed) this.toggleCollapsed();
    this.setTab("player");
    this.input.focus();
  }

  private makeTab(tab: ChatTab, label: string): TabView {
    const tabButton = button(label, tab === this.active ? "chat-tab active" : "chat-tab");
    const badge = el("span", "chat-badge hidden");
    tabButton.appendChild(badge);
    tabButton.addEventListener("click", () => {
      if (tab === this.active) this.toggleCollapsed();
      else {
        if (this.collapsed) this.toggleCollapsed();
        this.setTab(tab);
      }
    });
    return { button: tabButton, badge, unread: 0 };
  }

  private setTab(tab: ChatTab): void {
    this.active = tab;
    this.log.dataset.tab = tab;
    for (const [key, view] of Object.entries(this.tabs) as [ChatTab, TabView][]) {
      view.button.classList.toggle("active", key === tab);
    }
    this.clearUnread(tab);
    this.log.scrollTop = this.log.scrollHeight;
  }

  private send(): void {
    const text = this.input.value.trim();
    if (!text) return;
    net.send("chat", { text });
    this.input.value = "";
    if (this.active !== "player") this.setTab("player");
  }

  private add(msg: ChatMsg, countUnread: boolean): void {
    const tab: ChatTab = msg.kind === "system" ? "system" : "player";
    const line = el("div", `chat-line ${tab}`);
    if (msg.kind === "system") {
      line.textContent = msg.text;
    } else {
      const nick = el("span", "nick", `${msg.nickname ?? "?"}: `);
      const text = el("span", undefined, msg.text);
      line.append(nick, text);
    }

    const nearBottom = this.log.scrollHeight - this.log.scrollTop - this.log.clientHeight < 40;
    this.log.appendChild(line);
    while (this.log.childElementCount > 200) this.log.removeChild(this.log.firstChild!);
    if (nearBottom) this.log.scrollTop = this.log.scrollHeight;

    if (countUnread && (this.collapsed || tab !== this.active)) {
      const view = this.tabs[tab];
      view.unread++;
      view.badge.textContent = view.unread > 99 ? "99+" : String(view.unread);
      view.badge.classList.remove("hidden");
    }
  }

  private clearUnread(tab: ChatTab): void {
    const view = this.tabs[tab];
    view.unread = 0;
    view.badge.classList.add("hidden");
  }

  private toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
    this.root.classList.toggle("collapsed", this.collapsed);
    this.collapseBtn.textContent = this.collapsed ? "▴" : "▾";
    this.collapseBtn.title = this.collapsed ? "Expand chat" : "Collapse chat";
    if (!this.collapsed) {
      this.clearUnread(this.active);
      this.log.scrollTop = this.log.scrollHeight;
    }
  }
}
